import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { FormsModule } from '@angular/forms';
import { QaCustomerEmailKey, QaFixtureService, QaFixtureStatus } from '@app/contacts/shared/qa-fixture.service';

/**
 * TVG-359: the pre-production QA block on a fixture contact's page.
 *
 * The quote email can otherwise only be triggered by the WordPress front end
 * or by an API call with a token, so the 20 October 2026 QA run could not test
 * TVG-316, TVG-327 or TVG-328 from a browser at all. This is the smallest way
 * to unblock that: one button on the contact already being looked at, calling
 * the TVG-347 endpoint that was built for exactly this, which in turn sends
 * whichever quote email the website would send right now. No email is rebuilt
 * here and no wording, routing or link is duplicated.
 *
 * Nothing renders until the server confirms both that the QA tooling is
 * available in this environment and that this contact is a seeded fixture. The
 * status call 404s on production, so the block cannot appear there, and the
 * send endpoint refuses any address that is not a fixture, so the button
 * cannot be pointed at a real customer.
 */
@Component({
  selector: 'app-contact-qa-tools',
  standalone: true,
  imports: [ButtonComponent, FormsModule],
  templateUrl: './contact-qa-tools.component.html',
})
export class ContactQaToolsComponent implements OnInit, OnChanges {
  @Input({ required: true }) contactEmail!: string;

  protected status: QaFixtureStatus | null = null;
  protected sending = false;
  protected result: string | null = null;
  protected sendError: string | null = null;

  /** TVG-379: fresh copies of the customer emails, judged against today's layout. */
  protected readonly customerEmails: { key: QaCustomerEmailKey; label: string }[] = [
    { key: 'first_contact', label: 'First contact email (Email Template 1)' },
    { key: 'follow_up_template', label: 'Enquiry follow up (compose template)' },
    { key: 'enquiry_follow_up_1', label: 'Automatic follow up 1' },
    { key: 'enquiry_follow_up_2', label: 'Automatic follow up 2' },
    { key: 'post_viewing', label: 'Post viewing email' },
  ];
  protected customerEmail: QaCustomerEmailKey = 'first_contact';

  constructor(private qaFixtureService: QaFixtureService) {}

  ngOnInit(): void {
    this.loadStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactEmail'] && !changes['contactEmail'].firstChange) {
      this.status = null;
      this.result = null;
      this.sendError = null;
      this.loadStatus();
    }
  }

  /** Shown only where the tooling exists and the contact is a fixture. */
  protected get visible(): boolean {
    return this.status?.available === true && this.status.is_fixture_contact === true;
  }

  protected get mailCatcherUrl(): string {
    return this.status?.mail_catcher ?? '';
  }

  protected sendQuoteEmail(): void {
    if (this.sending || !this.visible) {
      return;
    }

    this.sending = true;
    this.result = null;
    this.sendError = null;

    this.qaFixtureService.sendQuoteEmail(this.contactEmail).subscribe({
      next: (response) => {
        this.sending = false;
        this.result = `${response.data.email} sent to ${response.data.sent_to} for ${response.data.venue}. Open the mail catcher to read it.`;
      },
      error: (error) => {
        this.sending = false;
        this.sendError = error?.error?.message || 'Failed to send the quote email';
      },
    });
  }

  protected sendCustomerEmail(): void {
    if (this.sending || !this.visible) {
      return;
    }

    this.sending = true;
    this.result = null;
    this.sendError = null;

    this.qaFixtureService.sendCustomerEmail(this.contactEmail, this.customerEmail).subscribe({
      next: (response) => {
        this.sending = false;
        const venue = response.data.venue ? ` for ${response.data.venue}` : '';
        this.result = `${response.data.email} sent to ${response.data.sent_to}${venue}. Open the mail catcher to read it.`;
      },
      error: (error) => {
        this.sending = false;
        this.sendError = error?.error?.message || 'Failed to send the email';
      },
    });
  }

  private loadStatus(): void {
    if (!this.contactEmail) {
      return;
    }

    this.qaFixtureService.status(this.contactEmail).subscribe({
      // Any failure — 404 on production, 403 for a customer — leaves the block
      // hidden. It is never shown on the strength of a guess.
      next: (response) => (this.status = response.data),
      error: () => (this.status = null),
    });
  }
}
